import dayjs, { Dayjs } from "dayjs";

/**
 * Metric derivation for the Performance panel.
 *
 * Since TDC-1676 the API does the counting: one request returns the totals and
 * one row per day for the window, already narrowed by project / source and
 * bucketed in the viewer's time zone (`GET /brands/:id/s3file/:fileId/analytics`,
 * lib/videoAnalyticsSummary in the API). The three raw-row reads this panel used
 * to make — every play, every per-second duration row, every embed pageview —
 * are gone; a busy video's payload was tens of megabytes and the base query
 * took staging down.
 *
 * Kept page-local, like the panel itself: `pages/admin/videoAnalytics/` is still
 * mounted by the V1 detail page on /videos/ugc, /videos/xgc and /my-videos.
 */

export type ChartRange = [Dayjs, Dayjs];

/** The API's summary payload. Days are `YYYY-MM-DD` in `tz`. */
export interface AnalyticsPayload {
  file_id: number;
  brand_id: number;
  tz: string;
  /** The window actually counted — the video's first event → today when none was asked for. */
  from: string;
  to: string;
  /** First event of any kind under the current filters; null when there is none. */
  earliest: string | null;
  earliest_day: string | null;
  filters: { project: number | null; source: string | null };
  totals: {
    impressions: number;
    plays: number;
    /** One duration row per second watched — the count IS the watch time. */
    watch_seconds: number;
    avg_play_seconds: number;
  };
  daily: Array<{ date: string; plays: number; watch_seconds: number; impressions: number }>;
}

export interface DayPoint {
  date: Dayjs;
  /**
   * Seconds watched that day. This is the series the chart plots: the same
   * "Seconds" series the old Analytics graph drew, now counted server-side.
   */
  seconds: number;
  plays: number;
  impressions: number;
}

export interface Metrics {
  impressions: number;
  plays: number;
  watchSeconds: number;
  avgPlaySeconds: number;
  series: DayPoint[];
  /** Earliest event of any kind, or null when the payload is empty. */
  earliest: Dayjs | null;
  hasAnyData: boolean;
}

/** The zone the request buckets days in — the viewer's, so chart days are their days. */
export const viewerTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** A range bound as the API's `from` / `to` query value. */
export const dayParam = (d: Dayjs): string => d.format("YYYY-MM-DD");

/** The window the payload covers, as a range. */
export const payloadRange = (data: AnalyticsPayload): ChartRange => [
  dayjs(data.from).startOf("day"),
  dayjs(data.to).startOf("day"),
];

/** Earliest event of any kind — drives the "All time" preset and the calendar floor. */
export const earliestEvent = (data?: AnalyticsPayload): Dayjs | null =>
  data?.earliest_day ? dayjs(data.earliest_day).startOf("day") : null;

export const computeMetrics = (data: AnalyticsPayload | undefined): Metrics => {
  const empty: Metrics = {
    impressions: 0,
    plays: 0,
    watchSeconds: 0,
    avgPlaySeconds: 0,
    series: [],
    earliest: null,
    hasAnyData: false,
  };
  if (!data) return empty;

  const totals = data.totals || { impressions: 0, plays: 0, watch_seconds: 0, avg_play_seconds: 0 };
  const series: DayPoint[] = (data.daily || []).map((d) => ({
    date: dayjs(d.date).startOf("day"),
    seconds: d.watch_seconds,
    plays: d.plays,
    impressions: d.impressions,
  }));
  const earliest = earliestEvent(data);

  return {
    impressions: totals.impressions,
    plays: totals.plays,
    watchSeconds: totals.watch_seconds,
    avgPlaySeconds: totals.plays ? totals.watch_seconds / totals.plays : 0,
    series,
    earliest,
    // "Has data" is about the video, not the window: a video with history and a
    // quiet week still shows its tiles and chart, not the "No views yet" card.
    hasAnyData: earliest !== null,
  };
};

// ------------------------------------------------------------- formatting

/** `857`, `1,234`. */
export const formatCount = (n: number): string => Math.round(n).toLocaleString();

/**
 * `1h 45m` / `45m 12s` / `12s`. Replaces `formatDuration`, which emitted
 * `1.03 min` and switched units per tile independently.
 */
export const formatWatchTime = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/**
 * How far back a range may reach when a video has NO recorded events. One
 * constant, used by the "All time" preset, the calendar's `minDate` AND the
 * label — they used to disagree (89 days, two years, and "no floor" respectively),
 * so pressing "All time" on an empty video produced a 90-day window whose pill
 * then read "last 90 days": the exact literal the redesign set out to remove.
 */
export const rangeFloor = (earliest: Dayjs | null): Dayjs =>
  (earliest || dayjs().subtract(2, "year")).startOf("day");

/**
 * The pill beside the heading, derived rather than the hardcoded "last 90 days".
 *
 * It has to read BOTH ends. Deciding "all time" from `start` alone labelled any
 * window that merely began before the first event as lifetime — so a one-week range
 * over the first week of traffic read "all time" above four tiles counting a week.
 * `days <= 1` likewise called a single day "today" whichever day it was. That pill
 * is the panel's only statement of what window the numbers cover.
 */
export const describeRange = (range: ChartRange, earliest: Dayjs | null): string => {
  const [start, end] = range;
  const today = dayjs().startOf("day");
  const from = start.startOf("day");
  const to = end.startOf("day");
  const openEnded = !to.isBefore(today);
  const days = to.diff(from, "day") + 1;

  if (openEnded && !from.isAfter(rangeFloor(earliest))) return "all time";
  if (days <= 1) return from.isSame(today) ? "today" : from.format("MMM D, YYYY");
  if (openEnded) return `last ${days} days`;
  return `${from.format("MMM D")} – ${to.format("MMM D, YYYY")}`;
};
